import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AddressesByBboxParams, AddressesByBboxResponse } from '@shared/house-connections';
import { FeatureCollection } from 'geojson';
import { firstValueFrom, from, Observable, scan, startWith, map, switchMap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CoreModule } from '../core.module';
import { ExtraDetails, KlicAttachment } from '../interfaces/attachment.interface';
import { Design } from '../interfaces/design.interface';
import { DesignLayer } from '../interfaces/design-layer.interface';
import { ExportConfiguration, ExportFilter, ExportTypes } from '../interfaces/export-types.enum';
import { MrklicFile } from '../interfaces/file.interface';
import { Log } from '../interfaces/log.interface';
import { UploadLogActionType } from '../constants';
import { Order, OrderItem } from '../interfaces/order-item.interface';
import {
  KvK,
  KvKAddress,
  MoneybirdContact,
  Organisation,
  OrganisationAdminStatistics,
} from '../interfaces/organisation.interface';
import { CreditLedgerEntry } from '../interfaces/credit-ledger.interface';
import { PaginatedResponse } from '../interfaces/pagination.interface';
import { PgBossJob } from '../interfaces/pgboss-job.interface';
import { Department } from '../interfaces/department.interface';
import { Project, ProjectReportData } from '../interfaces/project.interface';
import { MrKLICSubscription, SubscriptionType } from '../interfaces/subscription.interface';
import { UploadState } from '../interfaces/upload-state.interface';
import { Upload } from '../interfaces/upload.interface';
import { Role, User } from '../interfaces/user.interface';
import { KlicMetadataStateModel } from '../states/models/klic-metadata.state.model';
import { KlicNetworkObject, KlicPrecaution } from '../states/models/selection.state.model';
import { SendVerificationSMSParams, UpdateUserParams } from '../states/models/users.state.model';
import { RiskAssessment } from './../interfaces/risk-assessment.interface';
import { FMEDownloadTypes } from '@shared/export-types';
import { AdminStatistics } from '@shared/admin-statistics';

@Injectable({
  providedIn: CoreModule,
})
export class ApiService {
  private baseUrl = environment.apiBaseUrl + '/api/v1';
  constructor(private http: HttpClient) {}

  public upload(): (source: Observable<HttpEvent<unknown>>) => Observable<UploadState> {
    const initialState: UploadState = { state: 'PENDING', progress: 0 };

    const calculateUploadState = (upload: UploadState, event: HttpEvent<unknown>): UploadState => {
      if (this.isHttpProgressEvent(event)) {
        return {
          progress: event.total ? Math.round((100 * event.loaded) / event.total) : upload.progress,
          state: 'IN_PROGRESS',
        };
      }
      if (this.isHttpResponse(event)) {
        return {
          progress: 100,
          state: 'DONE',
          body: event.body,
        };
      }
      return upload;
    };

    return (source) => source.pipe(scan(calculateUploadState, initialState));
  }

  public isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
    return event.type === HttpEventType.Response;
  }

  public isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
    return event.type === HttpEventType.UploadProgress;
  }
  /* Subscriptions (currently, only the admin can use these routes */
  public createSubscription(createSubscriptionParams: {
    subscriptionTypeName: string;
    organisationId: string;
    status: string;
    customAvailableSlots: number;
    customPricePerPeriod: number | null;
    validFrom: string;
    validUntil?: string;
    reset_credits_after?: string;
  }) {
    return this.http.post<MrKLICSubscription>(`${this.baseUrl}/subscriptions`, createSubscriptionParams);
  }

  public updateSubscription(
    subscriptionId: string,
    updateParams: {
      status?: string;
      customAvailableSlots?: number;
      customPricePerPeriod?: number;
      topUpPricePerCredit?: number;
      reset_credits_after?: string;
    }
  ) {
    return this.http.patch<void>(`${this.baseUrl}/subscriptions/${subscriptionId}`, updateParams);
  }

  public cancelSubscription(subscriptionId: string) {
    return this.http.delete<void>(`${this.baseUrl}/subscriptions/${subscriptionId}`);
  }

  /* Organisations */
  public listOrganisations() {
    return this.http.get<Organisation[]>(`${this.baseUrl}/organisations`);
  }

  public getOrganisationById(organisationId: string) {
    return this.http.get<Organisation>(`${this.baseUrl}/organisations/${organisationId}`);
  }

  public setGebiedsinformatieAanvragenScheduleEnabled(organisationId: string, enabled: boolean) {
    return this.http.put<Organisation>(
      `${this.baseUrl}/organisations/${organisationId}/gebiedsinformatie-aanvragen/schedule`,
      { enabled }
    );
  }

  public setAutomatischeRisicoInventarisatieEnabled(organisationId: string, enabled: boolean) {
    return this.http.put<Organisation>(`${this.baseUrl}/organisations/${organisationId}/automatische-risico-inventarisatie`, { enabled });
  }

  public startGebiedsinformatieAanvragenBackfill(organisationId: string) {
    return this.http.post<{ organisationId: string; created: number }>(
      `${this.baseUrl}/organisations/${organisationId}/jobs/gebiedsinformatie-aanvragen/backfill`,
      {}
    );
  }

  public getAdminStatistics() {
    return this.http.get<AdminStatistics>(`${this.baseUrl}/admin-statistics`);
  }

  public getOrganisationUsageStatistics(organisationId: string) {
    return this.http.get<OrganisationAdminStatistics[]>(
      `${this.baseUrl}/organisations/${organisationId}/usage-statistics`
    );
  }

  public updateOrganisation(
    organisationId: string,
    organisationUpdateParams: Partial<Organisation>,
    fileIds: string[] | undefined
  ) {
    return this.http.put<Organisation>(`${this.baseUrl}/organisations/${organisationId}`, {
      ...organisationUpdateParams,
      fileIds,
    });
  }

  public updateMoneybirdContactId(organisationId: string, moneybirdContactId: string | null) {
    return this.http.put<Organisation>(`${this.baseUrl}/organisations/${organisationId}/moneybird-contact-id`, {
      moneybirdContactId,
    });
  }

  public listUploadsForOrganisation(params: {
    organisationId: string;
    params: {
      id?: string;
      projectId?: string;
      department?: string;
      statusses?: string[];
      q?: string;
      aanvraagSoort?: string;
      keepActive?: boolean;
      openEvs?: boolean;
      validFromStart?: string;
      validFromEnd?: string;
      page?: number;
      limit?: number;
    };
  }) {
    const queryParams: Record<string, any> = { ...params.params };
    if (queryParams['statusses']) {
      queryParams['statusses'] = queryParams['statusses'].join(',');
    }
    return this.http.get<PaginatedResponse<Upload>>(`${this.baseUrl}/organisations/${params.organisationId}/uploads`, {
      params: queryParams,
    });
  }

  public getUpload(params: { organisationId: string; uploadId: string }) {
    return this.http.get<Upload>(`${this.baseUrl}/organisations/${params.organisationId}/uploads/${params.uploadId}`);
  }

  public requestUploadsExcelExport(params: {
    organisationId: string;
    emailTo: string;
    userName: string;
    params: {
      projectId?: string;
      statusses?: string[];
      q?: string;
      aanvraagSoort?: string;
      evCriteria?: string;
      validFromStart?: string;
      validFromEnd?: string;
    };
  }) {
    const queryParams: Record<string, any> = { ...params.params, emailTo: params.emailTo, userName: params.userName };
    if (queryParams['statusses']) {
      queryParams['statusses'] = queryParams['statusses'].join(',');
    }
    return this.http.get(`${this.baseUrl}/organisations/${params.organisationId}/uploads-export`, {
      params: queryParams,
    });
  }

  public listUsersInOrganisation(params: { organisationId: string }) {
    return this.http.get<User[]>(`${this.baseUrl}/organisations/${params.organisationId}/users`);
  }

  public listProjectsInOrganisation(params: { organisationId: string }) {
    return this.http.get<Project[]>(`${this.baseUrl}/organisations/${params.organisationId}/projects`);
  }

  public createProjectInOrganisation(
    organisationId: string,
    project: {
      name: string;
      organisationId: string;
      fileIds: string[];
      chief_executor_id: string | null;
      department: string | null;
    }
  ) {
    return this.http.post<Project>(`${this.baseUrl}/organisations/${organisationId}/projects`, project);
  }

  public updateProjectInOrganisation(
    organisationId: string,
    project: {
      name: string;
      id: string;
      description: string | null;
      department: string | null;
      code: string | null;
      fileIds: string[];
      chief_executor_id?: string | null;
    }
  ) {
    return this.http.put<Project>(`${this.baseUrl}/organisations/${organisationId}/projects/${project.id}`, {
      name: project.name,
      code: project.code,
      description: project.description,
      department_name: project.department,
      fileIds: project.fileIds,
      chief_executor_id: project.chief_executor_id,
    });
  }

  public updateProjectKeepActiveDefault(organisationId: string, projectId: string, keepActiveDefault: boolean) {
    return this.http.put<Project>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/keep-active-default`,
      { keepActiveDefault }
    );
  }

  public deleteProjectInOrganisation(organisationId: string, projectId: string) {
    return this.http.delete<void>(`${this.baseUrl}/organisations/${organisationId}/projects/${projectId}`);
  }

  public renameDepartment(organisationId: string, departmentId: string, name: string) {
    return this.http.put<Department>(`${this.baseUrl}/organisations/${organisationId}/departments/${departmentId}`, {
      name,
    });
  }

  public deleteDepartment(organisationId: string, departmentId: string) {
    return this.http.delete<void>(`${this.baseUrl}/organisations/${organisationId}/departments/${departmentId}`);
  }

  public listDepartments(organisationId: string) {
    return this.http.get<Department[]>(`${this.baseUrl}/organisations/${organisationId}/departments`);
  }

  public createDepartment(organisationId: string, name: string) {
    return this.http.post<Department>(`${this.baseUrl}/organisations/${organisationId}/departments`, { name });
  }

  public getProjectDesign(organisationId: string, projectId: string) {
    return this.http.get<Design[]>(`${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/designs`);
  }

  public createProjectDesign(params: { organisationId: string; projectId: string; featureCollection: any }) {
    return this.http.post<Project>(
      `${this.baseUrl}/organisations/${params.organisationId}/projects/${params.projectId}/designs`,
      { featureCollection: params.featureCollection }
    );
  }

  // Design layers
  public listProjectDesignLayers(organisationId: string, projectId: string) {
    return this.http.get<DesignLayer[]>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/design-layers`
    );
  }

  public createProjectDesignLayer(organisationId: string, projectId: string, name: string, color?: string) {
    return this.http.post<DesignLayer>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/design-layers`,
      { name, color }
    );
  }

  public updateProjectDesignLayer(
    organisationId: string,
    projectId: string,
    layerId: string,
    name: string,
    color?: string
  ) {
    return this.http.put<DesignLayer>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/design-layers/${layerId}`,
      { name, color }
    );
  }

  public deleteProjectDesignLayer(organisationId: string, projectId: string, layerId: string) {
    return this.http.delete<{ success: boolean; designUploaded: boolean }>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/design-layers/${layerId}`
    );
  }

  public saveProjectDesignLayerFeatures(
    organisationId: string,
    projectId: string,
    layerId: string,
    featureCollection: any
  ) {
    return this.http.post<DesignLayer>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/design-layers/${layerId}/designs`,
      { featureCollection }
    );
  }

  public requestOrganisationsSubscription(params: {
    subscriptionTypeName: string;
    organisationId: string;
    customAvailableSlots: number | null;
  }) {
    return this.http.post<MrKLICSubscription>(
      `${this.baseUrl}/organisations/${params.organisationId}/subscriptions`,
      params
    );
  }

  public listOrderItemsInOrganisation(params: { organisationId: string }) {
    return this.http.get<OrderItem[]>(`${this.baseUrl}/organisations/${params.organisationId}/order-items`);
  }

  public getOrganisationRiskAssessment(organisationId: string) {
    return this.http.get<RiskAssessment>(`${this.baseUrl}/organisations/${organisationId}/risk-assessment`);
  }

  public createOrganisationRiskAssessment(organisationId: string, riskAssessment: { [key: string]: number }) {
    return this.http.post<RiskAssessment>(`${this.baseUrl}/organisations/${organisationId}/risk-assessment`, {
      riskAssessment,
    });
  }

  public updateOrganisationRiskAssessment(params: {
    organisationId: string;
    riskAssessmentId: string;
    riskAssessment: { [key: string]: number };
  }) {
    return this.http.put<RiskAssessment>(`${this.baseUrl}/organisations/${params.organisationId}/risk-assessment`, {
      riskAssessmentId: params.riskAssessmentId,
      riskAssessment: params.riskAssessment,
    });
  }

  public validateKadasterLogin(organisation: Organisation, user: User) {
    return this.http.post<any>(`${this.baseUrl}/organisations/${organisation.id}/kadaster-login-validation`, {
      organisation,
      user,
    });
  }

  // Credits
  public getCreditBalance(organisationId: string) {
    return this.http.get<number>(`${this.baseUrl}/organisations/${organisationId}/credit-balance`);
  }

  public getMoneybirdContact(organisationId: string) {
    return this.http.get<MoneybirdContact | null>(`${this.baseUrl}/organisations/${organisationId}/moneybird-contact`);
  }

  public addCredits(organisationId: string, credits: number) {
    return this.http.post(`${this.baseUrl}/organisations/${organisationId}/credits`, { credits });
  }

  public topUpCredits(organisationId: string, amount: number) {
    return this.http.post<any>(`${this.baseUrl}/organisations/${organisationId}/credits/top-up`, { amount });
  }

  public getCreditUsageForCurrentPeriod(organisationId: string) {
    return this.http.get<{
      creditUsage: { date: string; credit_usage: number }[];
      creditLedgerEntries: CreditLedgerEntry[];
      periodStart: string;
      periodEnd: string;
    }>(`${this.baseUrl}/organisations/${organisationId}/credit-usage`);
  }

  public getEntraIdConfig(organisationId: string) {
    return this.http.get<{ tenantId: string; organisationId: string; organisationName: string } | null>(
      `${this.baseUrl}/organisations/${organisationId}/entra-id-config`
    );
  }

  public saveEntraIdConfig(organisationId: string, tenantId: string) {
    return this.http.put<any>(`${this.baseUrl}/organisations/${organisationId}/entra-id-config`, { tenantId });
  }

  public deleteEntraIdConfig(organisationId: string) {
    return this.http.delete<{ success: boolean }>(`${this.baseUrl}/organisations/${organisationId}/entra-id-config`);
  }

  // Subscription Type
  public listSubscriptionTypes() {
    return this.http.get<SubscriptionType[]>(`${this.baseUrl}/subscription-types`);
  }

  /* Users */
  public listRoles() {
    return this.http.get<Role[]>(`${this.baseUrl}/roles`);
  }

  /* Users */
  public listUsers() {
    return this.http.get<User[]>(`${this.baseUrl}/users`);
  }

  public createUser(user: UpdateUserParams) {
    return this.http.post<User>(`${this.baseUrl}/users`, user);
  }

  public updateUser(userId: string, user: Partial<UpdateUserParams>) {
    return this.http.put<User>(`${this.baseUrl}/users/${userId}`, user);
  }

  public setUseOwnEmailForKadaster(userId: string, enabled: boolean) {
    return this.http.put<User>(`${this.baseUrl}/users/${userId}/use-own-email-for-kadaster`, { enabled });
  }

  public updateLoginPhoneNumber(token: string) {
    return this.http.put<{ success: boolean; access_token?: string }>(`${this.baseUrl}/users/login-phone-number`, {
      token,
    });
  }

  public deleteUser(userId: string) {
    return this.http.delete<void>(`${this.baseUrl}/users/${userId}`);
  }

  public getUserById(userId: string) {
    return this.http.get<User[]>(`${this.baseUrl}/users/${userId}`);
  }

  public emailExists(email: string) {
    return this.http.get<boolean>(`${this.baseUrl}/users/email/${email}`);
  }

  /* Uploads */
  public listUploadsForAdmin(params: { q?: string; status?: string; limit?: number; page?: number }) {
    return this.http.get<{ data: Upload[]; count: number }>(`${this.baseUrl}/uploads`, {
      params: params as any,
    });
  }

  public listUploadSourcesForOrganisation(organisationId: string) {
    return this.http.get<{ source: string; count: number }[]>(
      `${this.baseUrl}/organisations/${organisationId}/upload-sources`
    );
  }

  public listJobsByUploadId(uploadId: string) {
    return this.http.get<PgBossJob[]>(`${this.baseUrl}/uploads/${uploadId}/jobs`);
  }

  public deleteUpload(uploadId: string) {
    return this.http.delete<void>(`${this.baseUrl}/uploads/${uploadId}`);
  }

  public archiveUpload(uploadId: string) {
    return this.http.put<Upload>(`${this.baseUrl}/uploads/${uploadId}/archive`, {});
  }

  public unarchiveUpload(uploadId: string) {
    return this.http.put<Upload>(`${this.baseUrl}/uploads/${uploadId}/unarchive`, {});
  }

  public retryLastFailedJob(uploadId: string) {
    return this.http.put<{ success: boolean }>(`${this.baseUrl}/uploads/${uploadId}/jobs/retry-last-failed`, {});
  }

  public assignUploadToProject(params: { uploadId: string; projectId: string | null }) {
    return this.http.post<Upload>(`${this.baseUrl}/uploads/${params.uploadId}/projects/${params.projectId}`, null);
  }

  public updateUpload(params: { uploadId: string; uploadAttributes: Partial<Upload> & { fileIds?: string[] } }) {
    return this.http.put<Upload>(`${this.baseUrl}/uploads/${params.uploadId}`, params.uploadAttributes);
  }

  public createUpload(name: string, projectId: string | null) {
    return this.http.post<any>(`${this.baseUrl}/uploads`, { name, projectId });
  }

  public uploadKlicFileToS3(file: File, signedUrl: string) {
    const formData = new FormData();
    formData.append('file', file);
    const headers = new HttpHeaders({ 'Content-Type': file.type });
    return this.http
      .put<any>(signedUrl, file, { headers, reportProgress: true, observe: 'events' })
      .pipe(this.upload());
  }

  public uploadS3Done(uploadId: string) {
    return this.http.post<any>(`${this.baseUrl}/uploads/done`, { uploadId });
  }

  public async getDesignLayerNames(file: File, type: string): Promise<{ layers: string[]; fmeFileRef: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return firstValueFrom(
      this.http.post<{ layers: string[]; fmeFileRef: string }>(
        `${this.baseUrl}/uploads/designs/layers?type=${type}`,
        formData
      )
    );
  }

  public async uploadDesign(
    file: File | null,
    selectedUploadTypeUrl: string,
    selectedLayers?: string[],
    fmeFileRef?: string
  ) {
    let url = `${this.baseUrl}/uploads/designs?type=${selectedUploadTypeUrl}`;
    if (selectedLayers && selectedLayers.length > 0) {
      url += `&layers=${encodeURIComponent(JSON.stringify(selectedLayers))}`;
    }
    if (fmeFileRef) {
      url += `&fmeFileRef=${encodeURIComponent(fmeFileRef)}`;
    }
    const body = file
      ? (() => {
          const fd = new FormData();
          fd.append('file', file);
          return fd;
        })()
      : {};
    const data = await firstValueFrom(this.http.post<FeatureCollection>(url, body));
    return data;
  }

  public listVeiligGravenItemsForMap(
    uploadId: string,
    params: {
      lat: number;
      lng: number;
      buffer: number;
    }
  ) {
    return this.http.get<
      { id: string; title: string; type: string; risk_type?: string; eisVoorzorgsmaatregelId?: string | null }[]
    >(`${this.baseUrl}/uploads/${uploadId}/veilig-graven-items-intersecting-point`, {
      params,
    });
  }

  public listOtherActiveUploadsInProject(
    uploadId: string,
    params: {
      lat: number;
      lng: number;
      buffer: number;
    }
  ) {
    return this.http.get<Upload[]>(`${this.baseUrl}/uploads/${uploadId}/other-active-uploads`, {
      params,
    });
  }

  public createAndSendShareUrl(
    uploadId: string,
    params: {
      userIds: string[];
      redirectTo: string;
    }
  ) {
    return this.http.post<any>(`${this.baseUrl}/uploads/${uploadId}/share-url`, {
      ...params,
    });
  }

  /* KLIC-data */
  public listKlicAttachments(uploadId: string) {
    return this.http.get<KlicAttachment[]>(`${this.baseUrl}/klic/${uploadId}/klic-attachments`);
  }

  public listKlicExtraDetails(uploadId: string) {
    return this.http.get<ExtraDetails[]>(`${this.baseUrl}/klic/${uploadId}/klic-extra-detail`);
  }

  public listKlicMetaData(uploadId: string) {
    return this.http.get<KlicMetadataStateModel>(`${this.baseUrl}/klic/${uploadId}/metadata`);
  }

  public listProjectReportData(organisationId: string, projectId: string) {
    return this.http.get<ProjectReportData>(
      `${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/report-data`
    );
  }

  public listKlicNetworkObjects(
    uploadId: string,
    params: {
      lat: number;
      lng: number;
      buffer: number;
      utilityNetworks: string[];
      objectsToQuery: string[];
    }
  ) {
    return this.http.get<(KlicNetworkObject | KlicPrecaution)[]>(`${this.baseUrl}/klic/${uploadId}/network-objects`, {
      params,
    });
  }

  public getBasemapUrl(params: { uploadId: string }) {
    return `${this.baseUrl}/uploads/${params.uploadId}/basemap`;
  }

  public getBasemapDownloadUrl(params: { uploadId: string }) {
    return `${this.baseUrl}/uploads/${params.uploadId}/basemap_compressed`;
  }

  public getLeveringsbriefDownloadUrl(params: { uploadId: string }) {
    return `${this.baseUrl}/uploads/${params.uploadId}/leveringsbrief`;
  }

  public getKlicFileDownloadUrl(params: { uploadId: string; fileId: string }) {
    return `${this.baseUrl}/uploads/${params.uploadId}/download/${params.fileId}/attachment`;
  }

  public getKlicDownloadUrl(uploadId: string) {
    return this.http.get(`${environment.apiBaseUrl}/api/v1/uploads/${uploadId}/download`, { responseType: 'blob' });
  }

  public getKlicFilesZipUrl(uploadId: string) {
    return `${this.baseUrl}/klic/${uploadId}/files`;
  }

  public listUploadLogs(uploadId: string, actionTypes: UploadLogActionType[]) {
    return this.http.get<Log[]>(`${this.baseUrl}/uploads/${uploadId}/logs`, {
      params: { actionTypes },
    });
  }

  public getUserLogsByUploadId(organisationId: string, uploadId: string) {
    return this.http.get<Log[]>(`${this.baseUrl}/organisations/${organisationId}/uploads/${uploadId}/user-logs`);
  }

  public getUserLogsByProjectId(organisationId: string, projectId: string) {
    return this.http.get<Log[]>(`${this.baseUrl}/organisations/${organisationId}/projects/${projectId}/user-logs`);
  }

  public getExportsUrl(params: {
    organisationApiKey: string;
    urlType: 'wms' | 'wfs';
    projectId?: string;
    uploadId?: string;
  }) {
    if (params.uploadId) {
      return `${environment.apiBaseUrl}/exports/${params.organisationApiKey}/uploads/${params.uploadId}/klic/${params.urlType}`;
    }
    if (params.projectId) {
      return `${environment.apiBaseUrl}/exports/${params.organisationApiKey}/projects/${params.projectId}/klic/${params.urlType}`;
    }
    return `${environment.apiBaseUrl}/exports/${params.organisationApiKey}/klic/${params.urlType}?`;
  }

  public getLeveringDownloadUrlForUpload(params: { organisationId: string; uploadId: string }) {
    return this.http.get<{ leveringDownloadUrl: string | null }>(
      `${this.baseUrl}/organisations/${params.organisationId}/uploads/${params.uploadId}/offline`,
      {}
    );
  }

  /* KVK-API */
  public searchKvkCompanies(params: { searchTerm: string }) {
    return this.http.get<KvK.Company[]>(`${this.baseUrl}/kvk`, {
      params: {
        searchTerm: params.searchTerm,
      },
    });
  }

  public getKvkCompanyAddress(params: { vestigingsnummer: string }) {
    return this.http.get<KvKAddress>(`${this.baseUrl}/kvk/${params.vestigingsnummer}/address`);
  }

  public export(url: string) {
    return this.http.get(`${url}`, { responseType: 'blob' });
  }

  public emailFileExport(params: {
    exportType: ExportTypes;
    selectedUploadId?: string;
    exportConfiguration?: ExportConfiguration;
    exportFilters?: ExportFilter[];
    outputFormat: FMEDownloadTypes;
    emailTo: string;
    userName: string;
    veiligGravenContent?: string;
    projectId?: string;
    organisationId?: string;
    groupByNetbeheerder?: boolean;
  }) {
    // Prioritize upload endpoint if uploadId is provided
    if (params.selectedUploadId) {
      return this.http.get(`${this.baseUrl}/uploads/${params.selectedUploadId}/download-fme`, {
        params: {
          outputFormat: params.outputFormat,
          emailTo: params.emailTo,
          exportType: params.exportType,
          exportConfiguration: params.exportConfiguration || '',
          exportFilters: params.exportFilters || [],
          userName: params.userName,
          veiligGravenContent: params.veiligGravenContent || '',
          ...(params.groupByNetbeheerder !== undefined ? { GROUP_BY_NETBEHEERDER: params.groupByNetbeheerder } : {}),
        },
      });
    } else if (params.projectId && params.organisationId) {
      // Use project endpoint only if no uploadId is provided
      return this.http.get(
        `${this.baseUrl}/organisations/${params.organisationId}/projects/${params.projectId}/download-fme`,
        {
          params: {
            outputFormat: params.outputFormat,
            emailTo: params.emailTo,
            exportType: params.exportType,
            exportConfiguration: params.exportConfiguration || '',
            exportFilters: params.exportFilters || [],
            userName: params.userName,
            veiligGravenContent: params.veiligGravenContent || '',
            ...(params.groupByNetbeheerder !== undefined ? { GROUP_BY_NETBEHEERDER: params.groupByNetbeheerder } : {}),
          },
        }
      );
    } else {
      throw new Error('Either selectedUploadId or both projectId and organisationId must be provided');
    }
  }

  public emailProjectAreaExport(params: {
    projectId: string;
    organisationId: string;
    outputFormat: FMEDownloadTypes;
    emailTo: string;
    userName: string;
  }) {
    return this.http.get(
      `${this.baseUrl}/organisations/${params.organisationId}/projects/${params.projectId}/project-area`,
      {
        params: {
          outputFormat: params.outputFormat,
          email: params.emailTo,
          userName: params.userName,
        },
      }
    );
  }

  public createRiskGrid(selectedUploadId: string) {
    return this.http.post(`${this.baseUrl}/uploads/${selectedUploadId}/risk-grid`, {});
  }

  public deleteRiskGrid(selectedUploadId: string) {
    return this.http.delete(`${this.baseUrl}/uploads/${selectedUploadId}/risk-grid`);
  }

  /* Orders */
  public createOrder(body: {
    organisationId: string;
    orderedById: string;
    orderItems: any[];
    useOrganisationKadasterLogin: boolean;
    executionDate: string;
    projectId: string | null;
  }) {
    return this.http.post<{ checkoutUrl: string }>(`${this.baseUrl}/orders`, body);
  }

  public getOrder(orderId: string) {
    return this.http.get<Order>(`${this.baseUrl}/orders/${orderId}`);
  }

  public async chopArea(featureCollection: FeatureCollection, size: number, snap_to_area: boolean) {
    const data = await firstValueFrom(
      this.http.post<FeatureCollection>(`${this.baseUrl}/orders/chopped-request-area`, {
        featureCollection,
        size,
        snap_to_area,
      })
    );
    return data;
  }

  /* Order items */
  public listOrderItems(limit?: number, page?: number, q?: string, status?: string[]) {
    const params: any = {};
    if (limit !== undefined) params.limit = limit;
    if (page !== undefined) params.page = page;
    if (q !== undefined) params.q = q;
    if (status !== undefined) params.status = status;
    return this.http.get<{ data: OrderItem[]; count: number }>(`${this.baseUrl}/order-items`, { params });
  }

  public getOrderItem(orderItemId: string) {
    return this.http.get<OrderItem>(`${this.baseUrl}/order-items/${orderItemId}`);
  }

  public deleteOrderItem(orderItemId: string) {
    return this.http.delete<void>(`${this.baseUrl}/order-items/${orderItemId}`);
  }

  public updateOrderItemPostdata(orderItemId: string, postdata: unknown) {
    return this.http.patch<OrderItem>(`${this.baseUrl}/order-items/${orderItemId}/postdata`, { postdata });
  }

  public triggerKadasterRequest(orderItemId: string) {
    return this.http.get<any>(`${this.baseUrl}/order-items/${orderItemId}/trigger-kadaster-request`);
  }

  public getOrderItemByUploadId(uploadId: string) {
    return this.http.get<OrderItem>(`${this.baseUrl}/uploads/${uploadId}/order-items`);
  }

  /* Files */

  public uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);

    // iOS/WebKit has two bugs with file uploads:
    // 1. HttpResponse never fires when reportProgress is enabled.
    // 2. Files from iCloud/photo library are loaded lazily, truncating the multipart body.
    // Fix: read the file fully into memory first, then POST without progress tracking.
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      return from(file.arrayBuffer()).pipe(
        switchMap((buffer) => {
          // iOS camera/photo library always reports 'image.jpg' as the filename.
          // Generate a unique timestamped name in that case so files stay distinguishable.
          const ext = file.name.includes('.') ? file.name.split('.').pop() : 'jpg';
          const fileName =
            file.name === 'image.jpg' || file.name === 'image.png' || file.name === 'image.heic'
              ? `foto-${Date.now()}.${ext}`
              : file.name;
          const iosFormData = new FormData();
          iosFormData.append('file', new Blob([buffer], { type: file.type }), fileName);
          return this.http.post<MrklicFile>(`${this.baseUrl}/files`, iosFormData);
        }),
        map((body): UploadState => ({ state: 'DONE', progress: 100, body })),
        startWith<UploadState>({ state: 'PENDING', progress: 0 })
      );
    }

    const upload$ = this.http.post<MrklicFile>(`${this.baseUrl}/files`, formData, {
      reportProgress: true,
      observe: 'events',
    });

    return upload$.pipe(this.upload());
  }

  public deleteFile(fileId: string) {
    return this.http.delete(`${this.baseUrl}/files/${fileId}`);
  }

  public getFileDownloadUrl(fileId: string) {
    return `${this.baseUrl}/files/${fileId}`;
  }

  public referral(opts: { name: string; email: string; friendName: string; friendEmail: string; phone: string }) {
    return this.http.post(`${this.baseUrl}/referral`, opts);
  }

  public logActivity(userId: string) {
    return this.http.post(`${this.baseUrl}/users/${userId}/activity`, {});
  }

  /* Example Files */

  public getExportExampleUrl(exportFormat: string) {
    return `${this.baseUrl}/example-files?exportFormat=${exportFormat}`;
  }

  /* House connections / Locatieserver */
  public getAddressesByBbox(params: AddressesByBboxParams) {
    const queryParams: Record<string, string> = {
      minLat: params.minLat.toString(),
      minLon: params.minLon.toString(),
      maxLat: params.maxLat.toString(),
      maxLon: params.maxLon.toString(),
    };

    if (params.start !== undefined) {
      queryParams['start'] = params.start.toString();
    }

    if (params.rows !== undefined) {
      queryParams['rows'] = params.rows.toString();
    }

    return this.http.get<AddressesByBboxResponse>(`${this.baseUrl}/house-connections`, { params: queryParams });
  }

  /* LOGS */
  public createOpenKlicLog(log: { uploadId: string }) {
    return this.http.post<Log>(`${this.baseUrl}/logs/open-klic-melding`, log);
  }

  public createGeolocationLog(log: { uploadId: string; geolocation: GeolocationCoordinates }) {
    return this.http.post<Log>(`${this.baseUrl}/logs/geolocation-klic-viewer`, log);
  }

  // Auth

  public sendPhoneNumberVerificationSMS(userId: string, payload: SendVerificationSMSParams) {
    return this.http.post<User>(`${this.baseUrl}/auth/user/${userId}/send-verification-sms`, payload);
  }

  /* Veiligheidsgebieden */
  public getVeiligheidsgebieden() {
    return this.http.get<{ id: string; gmlId: string; blveId: number; geometry: any }[]>(
      `${this.baseUrl}/veiligheidsgebieden`
    );
  }
}
